import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import ReportRenderer from "../../components/Reports";
import { useParams } from "react-router-dom";
import useThemeContext from "../../hooks/useThemeSwitch";
import { setRootContainerWidth } from "../../reducers/layout/layoutReducer";
import { useStyles } from "./styles";
import useHasPermissions from "../../hooks/useHasPermissions";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import apiClient from "../../auth/apiClient"
import { useSelector, shallowEqual } from "react-redux";
import ValidationAccountService from "../../services/ValidationAccountService";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { LinearProgress } from "@material-ui/core";
import { USER_TYPES }  from "../../constants";

const ReportsPage = () => {
  const classes = useStyles();
  const { reportName } = useParams();
  const baseReportName = reportName?.replace(/Redshift$/i, "");
  const { layoutReducer } = useThemeContext();
  const [, layoutDispatch] = layoutReducer;
  const { hasPermissions } = useHasPermissions();
  const hasReportPermission = hasPermissions([baseReportName]);
  const FPAFeatureFlag = useFeatureFlag("Flexible Parking Account");
  const sharedAcctFeatureFlag = useFeatureFlag("Shared Accounts");
  const hotelIntegration = useFeatureFlag("Hotel Integrations");
  const rewardsFeatureFlag = useFeatureFlag("ONEpark.Rewards");
  const lprFeatureFlag = useFeatureFlag("LPR");
  const ReportQueueFlag = useFeatureFlag("Features.Report Queue");
  const ReportQueueV2Flag = useFeatureFlag("Features.Report Queue V2");
  
  const validationAccountService = useMemo(() => new ValidationAccountService(apiClient), []);
  const currentUserId = useSelector((state) => state.user.UserID);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const { facilityID } = useCurrentFacility();
  const entityScopeFacilityId = useSelector((state) => state.entityScope?.facilityGroupId);
  const scopeAwareFacilityID = entityScopeFacilityId || facilityID;
  const [isOnePassActiveForUser, setIsOnePassActiveForUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  const featureFlagPermission = (name) => {
    switch (name) {
      case "HotelAccessHolders":
        return hotelIntegration;
      case "HotelCharges":
        return hotelIntegration;
      case "ExpiringCreditCard":
      case "FPAStatement":
        return FPAFeatureFlag;
      case "SharedAccountActivity":
        return sharedAcctFeatureFlag;
      case "RewardsPointActivity":
        return rewardsFeatureFlag;
      case "LPRCameraScore":
        return lprFeatureFlag;
      default:
        return true;
    }
  };

  useEffect(() => {
    const checkOnePassEnabledStatus = async () => {
      if (reportName === "OnePassActivePasses") {
        setIsLoading(true);
        try {
          const response = await validationAccountService.getValidationAccountsForUser(
            scopeAwareFacilityID,
            currentUserId,
            true
          );
          const contractHolderIDs = response?.data.map(x => x.contractHolderID);
  
          const responses = await Promise.all(
            contractHolderIDs.map(account =>
              validationAccountService.getValidationAccountOnePassDetails(account, scopeAwareFacilityID)
            )
          );
  
          setIsOnePassActiveForUser(responses.some(res => res.data?.active));
        } catch (err) {
          console.error("Error getting user OnePass enabled status:", err);
        } finally {
          setIsLoading(false);
          hasLoadedOnce.current = true;
        }
      } else {
        setIsLoading(false);
        hasLoadedOnce.current = true;
      }
    };

    checkOnePassEnabledStatus();
  }, [reportName, scopeAwareFacilityID, currentUserId]);

  useEffect(() => {
    layoutDispatch({ type: setRootContainerWidth, payload: false });
  }, [layoutDispatch]);

  const isFeatureEnabled = featureFlagPermission(baseReportName);
  const canUserViewReport = currentUser.UserType === USER_TYPES.ValidationAccount && reportName === "OnePassActivePasses" ? isOnePassActiveForUser : hasReportPermission;
  const shouldRenderReport = isFeatureEnabled && canUserViewReport;
  const shouldShowPermissionError = isFeatureEnabled && !canUserViewReport;

  const memoizedReport = useMemo(() => {
    if (!shouldRenderReport) return null;
    return (
      <ReportRenderer
        className={classes.reportViewer}
        reportName={reportName}
        reportQueueEnabled={ReportQueueFlag}
        reportQueueV2Enabled={ReportQueueV2Flag}
      />
    );
  }, [shouldRenderReport, classes.reportViewer, reportName, ReportQueueFlag, ReportQueueV2Flag]);

  const showLoading = isLoading && !hasLoadedOnce.current;

  return (
    showLoading ? (
      <LinearProgress className={classes.loadingProgress} data-testid="loading-progress" />
    ) : (
      <>
        {!isFeatureEnabled && <h1>{reportName} is disabled</h1>}
        {shouldShowPermissionError && (
          <h1>You do not have permission to view {reportName}</h1>
        )}
        {memoizedReport}
      </>
    )
    );
};

export default memo(ReportsPage);
